import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { DenialReasonCell } from "../../components/network/DenialReasonCell";
import { ViolationDetailModal } from "../../components/network/ViolationDetailModal";
import { DeviceLink } from "../../components/devices/DeviceLink";
import { Alert } from "../../components/ui/Alert";
import { BulkActionBar } from "../../components/ui/BulkActionBar";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { DateField } from "../../components/ui/DateField";
import { DomainFavicon } from "../../components/ui/DomainFavicon";
import { FormField } from "../../components/ui/FormField";
import { Modal } from "../../components/ui/Modal";
import { PageHeader } from "../../components/ui/PageHeader";
import { SearchInput } from "../../components/ui/SearchInput";
import { SegmentedFilter } from "../../components/ui/SegmentedFilter";
import { Select } from "../../components/ui/Select";
import { EmptyState, ErrorState, LoadingState } from "../../components/ui/State";
import { type Column, Table } from "../../components/ui/Table";
import { Textarea } from "../../components/ui/Textarea";
import { useToast } from "../../components/ui/Toast";
import { ApiError } from "../../lib/api";
import {
  type NetworkViolationRow,
  networkViolationsApi,
} from "../../lib/api/network/violations";
import { formatDateTime } from "../../lib/format";
import { formatMessage, t } from "../../lib/i18n";
import { useLocale } from "../../lib/useLocale";

const PAGE_SIZE = 50;

type KindFilter = "all" | "domain" | "ipv4" | "ipv6" | "cidr";

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

function toIsoStart(value: string): string | undefined {
  if (!value) return undefined;
  return new Date(`${value}T00:00:00`).toISOString();
}

function toIsoEnd(value: string): string | undefined {
  if (!value) return undefined;
  return new Date(`${value}T23:59:59.999`).toISOString();
}

type GrantDuration = "1" | "4" | "8" | "24" | "72" | "168" | "permanent";

const GRANT_DURATIONS: GrantDuration[] = ["1", "4", "8", "24", "72", "168", "permanent"];

export function ViolationsPage() {
  useLocale();
  const qc = useQueryClient();
  const { push: pushToast } = useToast();
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState<KindFilter>("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [offset, setOffset] = useState(0);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantDuration, setGrantDuration] = useState<GrantDuration>("4");
  const [grantReason, setGrantReason] = useState("");

  const bulkGrant = useMutation({
    mutationFn: () =>
      networkViolationsApi.bulkGrant({
        violation_ids: Array.from(selectedIds),
        duration_hours: grantDuration === "permanent" ? null : Number(grantDuration),
        reason: grantReason || undefined,
      }),
    onSuccess: (data) => {
      setGrantOpen(false);
      setSelectedIds(new Set());
      setGrantReason("");
      void qc.invalidateQueries({ queryKey: ["violations"] });
      const tone =
        data.failed.length > 0
          ? "warning"
          : data.succeeded.length > 0
            ? "success"
            : "info";
      pushToast({
        tone,
        title: formatMessage("violations.bulk.grant.toast", {
          succeeded: String(data.succeeded.length),
          skipped: String(data.skipped.length),
          failed: String(data.failed.length),
        }),
      });
    },
    onError: (err) => {
      pushToast({
        tone: "danger",
        title: t("errors.generic"),
        body: translateError(err),
      });
    },
  });

  const filters = useMemo(
    () => ({
      q: search.trim() || undefined,
      kind: kind === "all" ? undefined : kind,
      from: advancedOpen ? toIsoStart(fromDate) : undefined,
      to: advancedOpen ? toIsoEnd(toDate) : undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [search, kind, fromDate, toDate, advancedOpen, offset],
  );

  const list = useQuery({
    queryKey: ["violations", filters],
    queryFn: () => networkViolationsApi.list(filters),
  });

  const rows = list.data?.items ?? [];

  // Raw per-attempt rows are purged after `raw_retention_days`; older history
  // only exists in the aggregated Insights tabs (rollup-backed). Warn when the
  // selected `from` reaches before that horizon so a sparse raw list reads as
  // expected, not as missing data.
  const retentionDays = list.data?.raw_retention_days;
  const beyondRetention = useMemo(() => {
    if (!retentionDays || !advancedOpen || !fromDate) return false;
    const horizon = new Date();
    horizon.setUTCHours(0, 0, 0, 0);
    horizon.setUTCDate(horizon.getUTCDate() - retentionDays);
    return new Date(`${fromDate}T00:00:00`) < horizon;
  }, [retentionDays, advancedOpen, fromDate]);

  const columns: Column<NetworkViolationRow>[] = [
    {
      key: "attempted_at",
      header: t("violations.column.time"),
      render: (r) => <span className="mono">{formatDateTime(r.attempted_at)}</span>,
    },
    {
      key: "url",
      header: t("violations.column.domain"),
      render: (r) => (
        <div className="domain-cell">
          <DomainFavicon value={r.destination} />
          <span className="domain-name domain-name--mono">{r.destination}</span>
        </div>
      ),
    },
    {
      key: "device",
      header: t("violations.column.device"),
      render: (r) => (
        <DeviceLink deviceId={r.device_id} deviceUid={r.device_uid} />
      ),
    },
    {
      key: "reason",
      header: t("violations.column.blockReason"),
      render: (r) => <DenialReasonCell row={r} />,
    },
    {
      key: "kind",
      header: t("violations.column.kind"),
      render: (r) => <span className="mono">{r.pool_kind}</span>,
    },
  ];

  const total = list.data?.total ?? 0;
  const limit = list.data?.limit ?? PAGE_SIZE;
  const hasPrev = offset > 0;
  const hasNext = offset + limit < total;

  const resetPaging = () => setOffset(0);

  return (
    <div>
      <PageHeader
        eyebrow={t("violations.eyebrow")}
        title={t("violations.title")}
        subtitle={t("violations.subtitle")}
      />

      <div className="violations-toolbar">
        <SearchInput
          className="violations-toolbar__search"
          placeholder={t("violations.searchPlaceholder")}
          value={search}
          onChange={(e) => {
            resetPaging();
            setSearch(e.target.value);
          }}
        />
        <SegmentedFilter<KindFilter>
          value={kind}
          onChange={(v) => {
            resetPaging();
            setKind(v);
          }}
          options={[
            { value: "all", label: t("common.all") },
            { value: "domain", label: "DOMAIN" },
            { value: "ipv4", label: "IPV4" },
            { value: "ipv6", label: "IPV6" },
            { value: "cidr", label: "CIDR" },
          ]}
        />
        <button
          type="button"
          className="btn btn--ghost btn--sm violations-toolbar__advanced"
          onClick={() => setAdvancedOpen((v) => !v)}
        >
          {advancedOpen
            ? t("violations.filter.hideAdvanced")
            : t("violations.filter.showAdvanced")}
        </button>
        <span className="violations-toolbar__total">
          {t("violations.totalLabel")}: <strong>{total}</strong>
        </span>
      </div>

      {advancedOpen && (
        <div className="violations-advanced">
          <Card>
            <div className="violations-advanced__fields">
            <FormField label={t("violations.filter.from")}>
              {(id) => (
                <DateField
                  id={id}
                  value={fromDate}
                  onChange={(v) => {
                    resetPaging();
                    setFromDate(v);
                  }}
                  ariaLabel={t("violations.filter.from")}
                />
              )}
            </FormField>
            <FormField label={t("violations.filter.to")}>
              {(id) => (
                <DateField
                  id={id}
                  value={toDate}
                  onChange={(v) => {
                    resetPaging();
                    setToDate(v);
                  }}
                  ariaLabel={t("violations.filter.to")}
                />
              )}
            </FormField>
          </div>
          </Card>
        </div>
      )}

      <div style={{ height: 12 }} />

      {beyondRetention && retentionDays && (
        <div style={{ marginBottom: 12 }}>
          <Alert tone="info" title={t("violations.retention.title")}>
            {formatMessage("violations.retention.body", {
              days: String(retentionDays),
            })}
          </Alert>
        </div>
      )}

      <BulkActionBar
        count={selectedIds.size}
        onClear={() => setSelectedIds(new Set())}
      >
        <Button size="sm" onClick={() => setGrantOpen(true)}>
          {t("violations.bulk.grant.action")}
        </Button>
      </BulkActionBar>

      <Card padding="none">
        {list.isLoading && <LoadingState />}
        {list.error && (
          <ErrorState titleKey="states.errorTitle" message={translateError(list.error)} />
        )}
        {list.data && (
          <Table
            rows={rows}
            columns={columns}
            rowKey={(row) => row.id}
            rowClassName="table-row--clickable"
            onRowClick={(row) => setSelectedEventId(row.id)}
            selectable
            selectedKeys={selectedIds}
            onSelectionChange={setSelectedIds}
            empty={
              <EmptyState titleKey="violations.emptyTitle" bodyKey="violations.emptyBody" />
            }
          />
        )}
      </Card>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
        <button
          type="button"
          className="btn btn--ghost"
          disabled={!hasPrev}
          onClick={() => setOffset(Math.max(0, offset - limit))}
        >
          {t("common.prev")}
        </button>
        <button
          type="button"
          className="btn btn--ghost"
          disabled={!hasNext}
          onClick={() => setOffset(offset + limit)}
        >
          {t("common.next")}
        </button>
      </div>

      <ViolationDetailModal
        eventId={selectedEventId}
        onClose={() => setSelectedEventId(null)}
      />

      <Modal
        open={grantOpen}
        onClose={() => setGrantOpen(false)}
        title={t("violations.bulk.grant.dialogTitle")}
      >
        <div className="stack">
          <p className="muted" style={{ margin: 0 }}>
            {formatMessage("violations.bulk.grant.dialogBody", {
              count: String(selectedIds.size),
            })}
          </p>
          <FormField label={t("violations.bulk.grant.duration")} required>
            {(fid) => (
              <Select
                id={fid}
                value={grantDuration}
                onChange={(e) => setGrantDuration(e.target.value as GrantDuration)}
              >
                {GRANT_DURATIONS.map((d) => (
                  <option key={d} value={d}>
                    {t(`violations.bulk.grant.durationOpt.${d}`)}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField
            label={t("violations.bulk.grant.reason")}
            hint={t("violations.bulk.grant.reasonHint")}
          >
            {(fid) => (
              <Textarea
                id={fid}
                value={grantReason}
                onChange={(e) => setGrantReason(e.target.value)}
                rows={3}
                placeholder={t("violations.bulk.grant.reasonPlaceholder")}
              />
            )}
          </FormField>
          <div
            className="row"
            style={{ justifyContent: "flex-end", gap: 8, marginTop: 4 }}
          >
            <Button variant="ghost" onClick={() => setGrantOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => bulkGrant.mutate()}
              loading={bulkGrant.isPending}
              disabled={selectedIds.size === 0}
            >
              {t("violations.bulk.grant.submit")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
